import { type EditorView } from 'prosemirror-view'
import { Node, MarkType } from 'prosemirror-model'
import { schema, marks } from 'prose-core'
import { ExtractStrongMarkSpecAttributes } from 'prose-core'
import { DEFAULT_MONOSPACE_FONT } from 'common/Prose/constants'

export const getFontAttributeConfig = (markType: MarkType) => {
  const config = fontAttributeMap[markType.name as keyof typeof schema.marks]
  if (!config)
    throw new Error(`Missing configuration for mark "${markType.name}", cannot determine which attributes to read`)
  return config
}

const transformFontWeight = (fontWeight: string) => {
  // Safari returns "normal" for the default font-weight
  if (fontWeight === 'normal') return 400
  return parseInt(fontWeight, 10)
}

const fontAttributeMap: {
  [k in keyof typeof marks]?: {
    attrName: keyof ExtractStrongMarkSpecAttributes<(typeof marks)[k]>
    cssAttrName: keyof React.CSSProperties
    transformFn?: (input: string) => any
  }
} = {
  font_size: {
    attrName: 'size',
    cssAttrName: 'fontSize',
    transformFn: parseInt,
  },
  font_family: {
    attrName: 'family',
    cssAttrName: 'fontFamily',
  },
  font_weight: {
    attrName: 'weight',
    cssAttrName: 'fontWeight',
    transformFn: transformFontWeight,
  },
  colored: {
    attrName: 'color',
    cssAttrName: 'color',
  },
  highlighted: {
    attrName: 'color',
    cssAttrName: 'backgroundColor',
  },
}

/**
 * Try to read the relevant mark attribute from a `node`, and if that fails fall back to reading
 * from the computed CSS for the dom node corresponding with the prose node at `pos`.
 */
export function determineFontAttribute(view: EditorView, node: Node, pos: number, markType: MarkType) {
  const { attrName, cssAttrName, transformFn = (i) => i } = getFontAttributeConfig(markType)

  const fontAttrFromMark = markType.isInSet(node.marks)?.attrs[attrName]

  if (fontAttrFromMark) {
    return fontAttrFromMark
  } else {
    let domNode
    try {
      domNode = view.domAtPos(pos).node
    } catch (e) {
      return null
    }
    /**
     * If your cursor is inside a block of text that has been deleted by an external collab event
     * (i.e. another tab), then domNode evaluates to a Text element, and not an HTMLElement
     *
     * The bug looked like this (before this fix):
     *  1. Write "hello world"
     *  2. Change the font-weight of the word "world" to "black" or whatever
     *  3. Put your cursor right at the end of "world"
     *  4. In another tab, open the same document
     *  5. Select " world" and press delete
     *  6. In the first tab, the editor crashes
     */

    if (view.state.doc.resolve(pos).parent.type === schema.nodes.code_block) {
      const computedStyle = window.getComputedStyle(domNode as Element)[cssAttrName as any]
      return computedStyle.match('Inter') ? DEFAULT_MONOSPACE_FONT : computedStyle
    }
    if (domNode instanceof Text) {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      domNode = domNode.parentElement!
    }
    if ((domNode as HTMLElement).querySelector('a')) {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      domNode = (domNode as HTMLElement).querySelector('a')!
    }
    if (domNode instanceof HTMLElement) {
      const cssAttr = window.getComputedStyle(domNode as Element)[cssAttrName as any]
      return transformFn(cssAttr) || null
    }
    return null
  }
}
